import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { runAiJob } from "@/lib/aiJobs";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import {
  Star, ShoppingBag, ExternalLink, Sparkles, Loader2,
  Filter, X, ImageIcon, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import SaveButton from "@/components/SaveButton";

const SORTS = [
  { value: "trending", label: "Trending" },
  { value: "rating", label: "Top rated" },
  { value: "price_asc", label: "Price · low to high" },
  { value: "price_desc", label: "Price · high to low" },
];

const fmtINR = (n) =>
  typeof n === "number" ? "₹" + n.toLocaleString("en-IN") : "—";

export default function AtelierPicks() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // current outfit detail
  const [sort, setSort] = useState("trending");
  const [styles, setStyles] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 25000]);
  const [minRating, setMinRating] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Refresh: clear cached seed + reroll a new set of Curated picks.
  const refreshCurated = () => {
    setRefreshTick((t) => t + 1);
    toast.success("Fetching a fresh curation…");
  };

  useEffect(() => {
    api.get("/atelier/meta").then(({ data }) => {
      setMeta(data);
      setPriceRange([data.price_min || 0, data.price_max || 25000]);
      // Pre-fill filters from user's profile preferences (gender + preferred styles)
      if (!prefilled && user) {
        const prefs = user.style_preferences || [];
        if (Array.isArray(prefs) && prefs.length) {
          const matching = prefs.filter((p) => (data.styles || []).includes(p));
          if (matching.length) setStyles(matching);
        }
        setPrefilled(true);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const params = useMemo(() => {
    const p = { sort };
    if (styles.length) p.style = styles.join(",");
    if (retailers.length) p.retailer = retailers.join(",");
    if (occasions.length) p.occasion = occasions.join(",");
    if (minRating > 0) p.min_rating = minRating;
    if (meta && priceRange[0] > (meta.price_min || 0)) p.min_price = priceRange[0];
    if (meta && priceRange[1] < (meta.price_max || 99999)) p.max_price = priceRange[1];
    // Gender-based filtering: male → men's, female → women's (unisex always allowed).
    // Backend accepts `gender` param to bias results. Non-binary / prefer-not shows all.
    if (user?.gender === "male" || user?.gender === "female") {
      p.gender = user.gender;
    }
    // Cache-buster to force a new curation on Refresh clicks.
    if (refreshTick) p._r = refreshTick;
    return p;
  }, [sort, styles, retailers, occasions, minRating, priceRange, meta, user, refreshTick]);

  useEffect(() => {
    setLoading(true);
    api.get("/atelier/outfits", { params }).then(({ data }) => {
      setItems(data);
    }).catch(() => toast.error("Could not load atelier outfits"))
      .finally(() => setLoading(false));
  }, [params]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const clearAll = () => {
    setStyles([]); setRetailers([]); setOccasions([]); setMinRating(0);
    if (meta) setPriceRange([meta.price_min || 0, meta.price_max || 25000]);
  };

  const activeFilterCount =
    styles.length + retailers.length + occasions.length +
    (minRating > 0 ? 1 : 0) +
    (meta && (priceRange[0] > (meta.price_min || 0) || priceRange[1] < (meta.price_max || 99999)) ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <Sparkles size={12} /> Curated · {items.length} looks
          </p>
          <h1 className="editorial-h1 mt-3">Atelier Picks.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Ready-made AI-edited outfits across styles. Try-on instantly, then shop the look across Amazon, Myntra, Ajio &amp; more.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={refreshCurated}
            className="rounded-none uppercase tracking-[0.2em] text-xs h-10"
            data-testid="atelier-refresh-btn"
            disabled={loading}
          >
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters((s) => !s)}
            className="rounded-none uppercase tracking-[0.2em] text-xs h-10"
            data-testid="atelier-filters-toggle"
          >
            <Filter size={14} className="mr-2" />
            Filters {activeFilterCount ? `· ${activeFilterCount}` : ""}
          </Button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="rounded-none w-[180px] h-10" data-testid="atelier-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <AnimatePresence>
        {showFilters && meta && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 border border-border p-6 overflow-hidden"
            data-testid="atelier-filters-panel"
          >
            <div className="grid md:grid-cols-3 gap-8">
              <FilterGroup title="Style" items={meta.styles}
                selected={styles}
                onToggle={(v) => toggle(styles, setStyles, v)}
                testIdPrefix="atelier-filter-style" />
              <FilterGroup title="Retailer" items={meta.retailers}
                selected={retailers}
                onToggle={(v) => toggle(retailers, setRetailers, v)}
                testIdPrefix="atelier-filter-retailer" />
              <FilterGroup title="Occasion" items={meta.occasions}
                selected={occasions}
                onToggle={(v) => toggle(occasions, setOccasions, v)}
                testIdPrefix="atelier-filter-occasion" />
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8 pt-6 border-t border-border">
              <div>
                <p className="eyebrow mb-3">Price · {fmtINR(priceRange[0])} – {fmtINR(priceRange[1])}</p>
                <Slider
                  min={meta.price_min || 0}
                  max={meta.price_max || 25000}
                  step={500}
                  value={priceRange}
                  onValueChange={setPriceRange}
                  data-testid="atelier-filter-price"
                />
              </div>
              <div>
                <p className="eyebrow mb-3">Minimum rating</p>
                <div className="flex gap-1">
                  {[0, 4, 4.5, 4.7].map((r) => (
                    <button
                      key={r}
                      onClick={() => setMinRating(r)}
                      data-testid={`atelier-filter-rating-${r}`}
                      className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border ${
                        minRating === r ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
                      }`}
                    >
                      {r === 0 ? "Any" : `${r}+`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={clearAll}
                className="text-xs uppercase tracking-[0.2em] underline underline-offset-4 text-muted-foreground"
                data-testid="atelier-filters-clear"
              >
                Clear filters
              </button>
              <Button
                onClick={() => setShowFilters(false)}
                className="rounded-none uppercase tracking-[0.2em] text-xs h-10"
                data-testid="atelier-filters-apply"
              >
                Apply
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="mt-12 text-center">
          <Loader2 className="mx-auto animate-spin" />
          <p className="eyebrow mt-4">Curating</p>
        </div>
      ) : items.length === 0 ? (
        <div className="mt-12 border border-dashed border-border p-16 text-center">
          <p className="font-serif text-3xl">No outfits match your filters.</p>
          <button onClick={clearAll} className="mt-4 text-xs uppercase tracking-[0.2em] underline underline-offset-4">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <OutfitCard
              key={it.id}
              outfit={it}
              index={i}
              onOpen={() => setOpen(it)}
            />
          ))}
        </div>
      )}

      <OutfitDetail outfit={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function FilterGroup({ title, items, selected, onToggle, testIdPrefix }) {
  return (
    <div>
      <p className="eyebrow mb-3">{title}</p>
      <div className="flex gap-2 flex-wrap">
        {items.map((v) => (
          <button
            key={v}
            onClick={() => onToggle(v)}
            data-testid={`${testIdPrefix}-${v.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 h-8 text-[11px] uppercase tracking-[0.18em] border ${
              selected.includes(v) ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function OutfitCard({ outfit, index, onOpen }) {
  const thumbs = (outfit.items || []).slice(0, 4);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      onClick={onOpen}
      data-testid={`atelier-card-${outfit.id}`}
      className="group cursor-pointer border border-border bg-card hover:border-foreground transition-colors"
    >
      <div className="aspect-[4/5] bg-muted overflow-hidden relative">
        <SaveButton
          itemType="atelier"
          itemId={outfit.id}
          snapshot={{ title: outfit.title, hero_image: outfit.hero_image, price: outfit.total_price_inr, retailer: outfit.primary_retailer, style: outfit.style_type }}
          variant="ghost"
        />
        <img
          src={outfit.hero_image}
          alt={outfit.title}
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
          onError={(e) => { e.target.style.opacity = "0.2"; }}
        />
        <div className="absolute top-3 left-3 px-2 py-1 bg-background/85 backdrop-blur text-[10px] uppercase tracking-[0.2em]">
          {outfit.style_type}
        </div>
        <div className="absolute top-3 right-3 px-2 py-1 bg-accent text-accent-foreground text-[10px] flex items-center gap-1">
          <Star size={9} fill="currentColor" /> {outfit.rating?.toFixed(1)}
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex gap-1.5">
          {thumbs.map((t, i) => (
            <div key={i} className="w-10 h-10 bg-white/90 backdrop-blur overflow-hidden border border-white/60">
              <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-serif text-xl leading-tight">{outfit.title}</p>
          <p className="text-sm font-medium whitespace-nowrap">{fmtINR(outfit.total_price_inr)}</p>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 uppercase tracking-[0.18em]">
          {outfit.primary_retailer} · {outfit.items?.length || 0} pieces · {outfit.occasion}
        </p>
      </div>
    </motion.div>
  );
}

function OutfitDetail({ outfit, onClose }) {
  const [busy, setBusy] = useState(false);

  if (!outfit) return null;

  const buyAll = () => {
    const urls = (outfit.items || []).map((i) => i.url).filter(Boolean);
    if (urls.length === 0) return;
    // Open primary buy_url first, then individual items
    if (outfit.buy_url) window.open(outfit.buy_url, "_blank", "noopener");
    urls.forEach((u, idx) => {
      setTimeout(() => window.open(u, "_blank", "noopener"), 120 * (idx + 1));
    });
    toast.success(`Opening ${urls.length} product page${urls.length === 1 ? "" : "s"}`);
  };

  const tryOn = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("background", "studio");
      // /atelier/…/tryon is now async — runAiJob polls the ai_jobs row so
      // Cloudflare's 100s response cap never kills the flow.
      const data = await runGated("tryons", () =>
        runAiJob(
          () => api.post(`/atelier/outfits/${outfit.id}/tryon`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
        ),
      );
      toast.success("Try-on ready! Find it in Try-On Studio.");
      // Best effort: navigate later. For now, just toast.
      if (data?.id) {
        window.location.href = "/tryon";
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "quota_exceeded") {
        toast.error(detail.message || "Daily quota reached");
      } else {
        toast.error(typeof detail === "string" ? detail : (e?.message || "Try-on failed — upload a body photo in Profile first"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!outfit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="atelier-detail"
      >
        <div className="grid md:grid-cols-2">
          <div className="aspect-square md:aspect-auto bg-muted overflow-hidden">
            <img
              src={outfit.hero_image}
              alt={outfit.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 md:p-8 flex flex-col">
            <DialogHeader>
              <p className="eyebrow">{outfit.style_type} · {outfit.occasion}</p>
              <DialogTitle className="font-serif text-3xl tracking-tight mt-2 text-left">
                {outfit.title}
              </DialogTitle>
            </DialogHeader>

            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Star size={14} fill="currentColor" /> {outfit.rating?.toFixed(1)}
              </span>
              <span className="text-muted-foreground text-xs uppercase tracking-[0.18em]">
                {outfit.reviews_count || 0} reviews
              </span>
            </div>

            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              {outfit.description}
            </p>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {(outfit.vibes || []).map((v) => (
                <span key={v} className="px-2.5 py-1 border border-border text-[10px] uppercase tracking-[0.18em]">
                  {v}
                </span>
              ))}
            </div>

            <div className="mt-6 border-t border-border pt-5">
              <p className="eyebrow mb-3">The pieces · {outfit.items?.length || 0}</p>
              <div className="grid grid-cols-2 gap-3">
                {(outfit.items || []).map((it, i) => (
                  <a
                    key={i}
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`atelier-item-${i}`}
                    className="border border-border hover:border-foreground transition-colors group"
                  >
                    <div className="aspect-square bg-muted overflow-hidden">
                      {it.image ? (
                        <img src={it.image} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-full h-full p-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-[11px] leading-tight line-clamp-2">{it.name}</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs font-medium">{fmtINR(it.price_inr)}</span>
                        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1">
                          {it.retailer} <ExternalLink size={9} />
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-auto pt-6 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="eyebrow">Total</p>
                <p className="font-serif text-3xl">{fmtINR(outfit.total_price_inr)}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={tryOn}
                  disabled={busy}
                  className="rounded-none uppercase tracking-[0.2em] text-xs h-11"
                  data-testid="atelier-tryon-btn"
                >
                  {busy
                    ? <><Loader2 size={14} className="animate-spin mr-2" /> Trying on</>
                    : <><Sparkles size={14} className="mr-2" /> Try-on</>}
                </Button>
                <Button
                  onClick={buyAll}
                  className="rounded-none uppercase tracking-[0.2em] text-xs h-11"
                  data-testid="atelier-buy-btn"
                >
                  <ShoppingBag size={14} className="mr-2" />
                  Shop the look
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
              Buying opens each item on its retailer site so you can add it to your cart there.
              Items shown are reference styles — retailer stock varies.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
